export function renderGenCreateContentServerActionTemplate({
  ctxTableVar,
  ctxCacheInvalidationFn,
  ctxCamelPlural,
  ctxSingular,
  kebabSingular,
  snapshotImport,
  lifecycleHookImport,
  markdownImport,
  createContext,
  beforeHook,
  createBeforePublishBlock,
  slugSourceValue,
  toKebabCase,
  ctxHtmlCreateBlock,
  ctxCreateMappings,
  ctxHasHtmlOutputContent,
  ctxHasDraftContent,
  ctxPlural,
  snapshotData,
  ctxSingularText,
  createResultContext,
  afterHook,
  createAfterPublishBlock,
  ctxCamelSingular
}: {
  ctxTableVar: string
  ctxCacheInvalidationFn: string
  ctxCamelPlural: string
  ctxSingular: string
  kebabSingular: string
  snapshotImport: string
  lifecycleHookImport: string
  markdownImport: string
  createContext: string
  beforeHook: string
  createBeforePublishBlock: string
  slugSourceValue: string
  toKebabCase: string
  ctxHtmlCreateBlock: string
  ctxCreateMappings: string
  ctxHasHtmlOutputContent: string
  ctxHasDraftContent: string
  ctxPlural: string
  snapshotData: string
  ctxSingularText: string
  createResultContext: string
  afterHook: string
  createAfterPublishBlock: string
  ctxCamelSingular: string
}): string {
  return `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${ctxTableVar} } from '@admin/db/schema'
import { desc } from 'drizzle-orm'
import { ${ctxCacheInvalidationFn} } from 'next/cache'
import { createEntityVersion, entityVersionsCacheTags } from '@admin/actions/entity-versions'
import { ${ctxCamelPlural}CacheTags } from './types'
import { get${ctxSingular}ById } from './get-${kebabSingular}-by-id'
import { getTouchedFieldNames } from '@admin/utils/audit/get-touched-field-names'
import {
  get${ctxSingular}SlugRetryLimit,
  is${ctxSingular}SlugConflict,
  resolveUnique${ctxSingular}Slug,
  ${snapshotImport}
} from './helpers'
${lifecycleHookImport}
import type { ${ctxSingular}CreateInput, ${ctxSingular}CreateResult, ${ctxSingular} } from './types'${markdownImport}

export async function create${ctxSingular}(input: ${ctxSingular}CreateInput): Promise<${ctxSingular}CreateResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])

  try {
    const actorId = actor.id
    const now = new Date().toISOString()
    const auditRequestMetadata = await getAuditRequestMetadata()
    const changedFields = getTouchedFieldNames(input as unknown as Record<string, unknown>)
    const lifecycleContext = ${createContext}
    ${beforeHook}
${createBeforePublishBlock}

    let result: (typeof ${ctxTableVar}.$inferSelect)[] | null = null
    for (let attempt = 0; attempt < get${ctxSingular}SlugRetryLimit(); attempt += 1) {
      try {
        result = await db.transaction(async (tx) => {
          const maxSortOrderResult = await tx
            .select({ maxOrder: ${ctxTableVar}.sortOrder })
            .from(${ctxTableVar})
            .orderBy(desc(${ctxTableVar}.sortOrder))
            .limit(1)
          const nextSortOrder = (maxSortOrderResult[0]?.maxOrder ?? 0) + 1
          const resolvedSlug = await resolveUnique${ctxSingular}Slug(tx, {
            requestedSlug: input.slug,
            sourceValue: ${slugSourceValue},
            fallbackPrefix: '${toKebabCase}'
          })
${ctxHtmlCreateBlock}
          const createdRows = await tx.insert(${ctxTableVar}).values({
${ctxCreateMappings},${ctxHasHtmlOutputContent}${ctxHasDraftContent}
            sortOrder: nextSortOrder,
            createdAt: now,
            updatedAt: now,
            createdBy: actorId,
            updatedBy: actorId
          }).returning()

          const createdRow = createdRows[0]
          if (createdRow?.id) {
            await createEntityVersion(tx, {
              entityType: '${ctxPlural}',
              entityId: createdRow.id,
              data: ${snapshotData},
              createdBy: actorId
            })
          }

          return createdRows
        })
        break
      } catch (error) {
        if (is${ctxSingular}SlugConflict(error) && attempt < get${ctxSingular}SlugRetryLimit() - 1) {
          continue
        }
        throw error
      }
    }

    if (!result) throw new Error('Failed to create ${ctxSingularText}')

    const created${ctxSingular}Id = result[0]?.id
    ${ctxCacheInvalidationFn}(${ctxCamelPlural}CacheTags.all)
    if (created${ctxSingular}Id !== null && created${ctxSingular}Id !== undefined) {
      ${ctxCacheInvalidationFn}(${ctxCamelPlural}CacheTags.byId(created${ctxSingular}Id))
      ${ctxCacheInvalidationFn}(entityVersionsCacheTags.for('${ctxPlural}', created${ctxSingular}Id))
    }
    const created${ctxSingular} =
      typeof created${ctxSingular}Id === 'string'
        ? await get${ctxSingular}ById(created${ctxSingular}Id)
        : null
    const created${ctxSingular}Data = created${ctxSingular} ?? (result[0] as unknown as ${ctxSingular})
    const created${ctxSingular}Context = ${createResultContext}
    ${afterHook}
${createAfterPublishBlock}

    return {
      success: true,
      ${ctxCamelSingular}: created${ctxSingular}Data
    }
  } catch (error) {
    console.error('Error creating ${ctxSingularText}:', error)
    throw new Error(error instanceof Error ? error.message : 'Failed to create ${ctxSingularText}')
  }
}
`
}
